import React, { useState } from 'react'
import { Box, FormControlLabel, IconButton, Popover, Checkbox, FormGroup, TextField, InputAdornment, Button, MenuItem, useTheme } from '@mui/material'
import { DragIndicatorRounded, FilterListRounded, ViewColumnRounded, ClearRounded, SearchRounded, Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material'
import Tile from '../../layout/Tile'
import { Column, ISmartTableFilter, Row, SmartTableToolbar } from './SmartTableDefinitions'
import { closestCenter, DndContext, DragEndEvent, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import DownloadIcon from '@mui/icons-material/Download'
import CSVManager from '../../../managers/CSVManager'

interface ToolbarProps<T extends object> {
    toolbar: SmartTableToolbar<T>
    columns: Column<T>[]
    setColumns: React.Dispatch<React.SetStateAction<Column<T>[]>>
    filters: ISmartTableFilter<T>[]
    setFilters: React.Dispatch<React.SetStateAction<ISmartTableFilter<T>[]>>
    search: string
    setSearch: React.Dispatch<React.SetStateAction<string>>
    rows: Row[]
}

export default function TableToolbar<T extends object>({ toolbar, columns, setColumns, filters, setFilters, search, setSearch, rows }: ToolbarProps<T>) {
    const [request, setRequest] = useState<'columns' | 'filters'>(null)
    const [anchor, setAnchor] = useState<HTMLElement>(null)
    const [activeButton, setActiveButton] = useState<'filters' | 'columns' | 'download' | null>(null)
    const theme = useTheme()
    const hasActiveFilters = filters.some(filter => filter.value !== '')

    const getButtonClass = (buttonType: 'filters' | 'columns' | 'download') => {
        let className = 'toolbar-button'

        if (buttonType === 'filters' && hasActiveFilters) {
            className += theme.palette.mode === 'dark' ? ' filters-active-dark-mode' : ' filters-active-light-mode'
        }
        if (buttonType === 'columns' && activeButton === 'columns') {
            className += ' columns-active'
        }

        return className
    }

    return (
        <Tile.Row justifyContent='flex-end' alignItems='center' spacing={1} className='smart-table-toolbar'>
            {toolbar.search && toolbar.search.length > 0 &&
                <SearchTool search={search} setSearch={setSearch} />}
            <IconButton
                onClick={e => {
                    setAnchor(e.currentTarget)
                    setRequest('filters')
                    setActiveButton('filters')
                }}
                className={getButtonClass('filters')}>
                <FilterListRounded />
            </IconButton>
            <IconButton
                onClick={e => {
                    setAnchor(e.currentTarget)
                    setRequest('columns')
                    setActiveButton('columns')
                }}
                className={getButtonClass('columns')}>
                <ViewColumnRounded />
            </IconButton>
            <IconButton
                onClick={() => {
                    const headers = columns.map(col => col.label)
                    const data = rows.map(row => row.cells.map(cell => cell.value))
                    CSVManager.downloadCSV(data, headers, toolbar?.export?.fileName)
                }}
                className='toolbar-button'>
                <DownloadIcon />
            </IconButton>
            <Popover
                open={Boolean(anchor)}
                anchorEl={anchor}
                onClose={() => {
                    setAnchor(null)
                    setActiveButton(null)
                }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
                <Box p={1} sx={{ maxHeight: 500, overflowY: 'auto' }}>
                    {request === 'filters' &&
                        <FiltersTool columns={columns} filters={filters} setFilters={setFilters} />}
                    {request === 'columns' &&
                        <ColumnsTool columns={columns} setColumns={setColumns} />}
                </Box>
            </Popover>
        </Tile.Row>
    )
}
interface FiltersToolProps<T extends object> {
    columns: Column<T>[]
    filters: ISmartTableFilter<T>[]
    setFilters: React.Dispatch<React.SetStateAction<ISmartTableFilter<T>[]>>
}

const FiltersTool = <T extends object>({ columns, filters, setFilters }: FiltersToolProps<T>) => {
    const handleAddFilter = () => {
        setFilters([...filters, { column: columns[0], operator: 'contains', value: '' }])
        setFilters([...filters, { column: columns[0], operator: 'equals', value: '' }])
        setFilters([...filters, { column: columns[0], operator: 'notContains', value: '' }])
        setFilters([...filters, { column: columns[0], operator: 'notEquals', value: '' }])
        setFilters([...filters, { column: columns[0], operator: 'greaterThan', value: '' }])
        setFilters([...filters, { column: columns[0], operator: 'lessThan', value: '' }])
    }

    const handleChangeFilter = (index: number, key: keyof ISmartTableFilter<T>, value: any) => {
        const newFilters = [...filters]
        newFilters[index] = { ...newFilters[index], [key]: value }
        setFilters(newFilters)
    }

    const handleDeleteFilter = (index: number) => {
        if (filters.length === 1) {
            handleChangeFilter(index, 'value', '')
        } else {
            const newFilters = filters.filter((_, i) => i !== index)
            setFilters(newFilters)
        }
    }

    return (
        <Box pt={2} pl={1}>
            {filters.map((filter, index) => (
                <Box key={index} display='flex' alignItems='center' mb={2}>
                    <TextField
                        size='small'
                        select
                        label='Where'
                        value={filter.column.id}
                        onChange={e => handleChangeFilter(index, 'column', columns.find(col => col.id === e.target.value))}
                        style={{ marginRight: 8, minWidth: 150 }}>
                        {columns.map(col => (
                            <MenuItem key={col.id} value={col.id}>{col.label}</MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        select
                        size='small'
                        label='Operator'
                        value={filter.operator}
                        onChange={e => handleChangeFilter(index, 'operator', e.target.value)}
                        style={{ marginRight: 8, minWidth: 120 }}>
                        <MenuItem value='contains'>Contains</MenuItem>
                        <MenuItem value='notContains'>Not contains</MenuItem>
                        <MenuItem value='equals'>Equals</MenuItem>
                        <MenuItem value='notEquals'>Not equals</MenuItem>
                        <MenuItem value='greaterThan'>Greater than</MenuItem>
                        <MenuItem value='lessThan'>Less than</MenuItem>
                    </TextField>
                    <TextField
                        label='Value'
                        size='small'
                        value={filter.value}
                        onChange={e => handleChangeFilter(index, 'value', e.target.value)}
                        style={{ marginRight: 8, minWidth: 120 }}
                    />
                    <IconButton onClick={() => handleDeleteFilter(index)}>
                        <DeleteIcon />
                    </IconButton>
                </Box>
            ))}
            <Button startIcon={<AddIcon />} onClick={handleAddFilter}>
                Add Filter
            </Button>
        </Box>
    )
}

interface ColumnsToolProps<T extends object> {
    columns: Column<T>[]
    setColumns: React.Dispatch<React.SetStateAction<Column<T>[]>>
}

const ColumnsTool = <T extends object>({ columns, setColumns }: ColumnsToolProps<T>) => {
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    )

    const onToggleColumn = (checked: boolean, c: Column<T>) => {
        const newColumns = columns.map(col =>
            col.id === c.id ? { ...col, hidden: !checked } : col
        )
        setColumns(newColumns)
    }

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event
        if (active.id !== over.id) {
            setColumns(prev => {
                const oldIndex = prev.findIndex(x => x.id === active.id)
                const newIndex = prev.findIndex(x => x.id === over.id)
                return arrayMove(prev, oldIndex, newIndex)
            })
        }
    }
    return (
        <FormGroup style={{ padding: 'var(--unit)' }}>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={columns} strategy={verticalListSortingStrategy}>
                    {columns.map(x => {
                        return (
                            <SortableItem key={x.id} id={x.id}>
                                <div style={{ maxWidth: 220 }}>
                                    <FormControlLabel
                                        control={<Checkbox checked={!x.hidden} onChange={(e, c) => onToggleColumn(c, x)} size='small' />}
                                        label={<div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 190 }}>{x.label}</div>}
                                    />
                                </div>
                            </SortableItem>
                        )
                    })}
                </SortableContext>
            </DndContext>
        </FormGroup>
    )
}

const SearchTool = ({ search, setSearch }: { search: string, setSearch: React.Dispatch<React.SetStateAction<string>> }) => {
    return (
        <TextField
            placeholder='Search'
            size='small'
            value={search}
            onChange={e => setSearch(e.target.value)}
            sx={{ width: '250px', marginRight: 'auto' }}
            InputProps={{
                startAdornment: (
                    <InputAdornment position='start'>
                        <SearchRounded />
                    </InputAdornment>
                ),
                endAdornment: (
                    <InputAdornment position='end'>
                        {search && (
                            <IconButton onClick={() => setSearch('')} edge='end'>
                                <ClearRounded />
                            </IconButton>
                        )}
                    </InputAdornment>
                )
            }}
        />
    )
}

const SortableItem = ({ children, id }: { children: React.ReactNode, id: string }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition
    } = useSortable({ id })

    const style = {
        transform: CSS.Transform.toString(transform),
        transition
    }

    return (
        <div ref={setNodeRef} style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', ...style }} {...attributes}>
            {children}
            <div style={{ cursor: 'move' }} {...listeners}>
                <DragIndicatorRounded />
            </div>
        </div>
    )
}
